/**
 * 알림 서비스 (DB + UI 전용)
 * 이메일 발송과 완전히 분리됨
 */

import { createBrowserClient } from '@/supabase'
import type {
  Notification,
  NotificationStatus,
  CreateNotificationInput,
} from './notification.types'

function validateCreateNotificationInput(input: CreateNotificationInput) {
  const hasTarget =
    Boolean(input.targetTeamId) ||
    Boolean(input.targetClientId) ||
    Boolean(input.targetProfileId)

  if (!hasTarget) {
    throw new Error('알림 생성 실패: 최소 1개 이상의 수신 대상(targetTeamId/targetClientId/targetProfileId)이 필요합니다.')
  }

  if (input.senderType === 'TEAM' && !input.senderTeamId) {
    throw new Error('알림 생성 실패: senderType=TEAM인 경우 senderTeamId가 필요합니다.')
  }

  if (input.senderType === 'CLIENT' && !input.senderClientId) {
    throw new Error('알림 생성 실패: senderType=CLIENT인 경우 senderClientId가 필요합니다.')
  }

  if (input.senderType === 'FREELANCER' && !input.senderProfileId) {
    throw new Error('알림 생성 실패: senderType=FREELANCER인 경우 senderProfileId가 필요합니다.')
  }

  if (input.counselId !== undefined && (!Number.isInteger(input.counselId) || input.counselId <= 0)) {
    throw new Error('알림 생성 실패: counselId는 1 이상의 정수여야 합니다.')
  }
}

/**
 * 알림 생성 (DB에만 저장)
 */
export async function createNotification(
  input: CreateNotificationInput
): Promise<Notification> {
  // 클라이언트에서만 실행
  if (typeof window === 'undefined') {
    throw new Error('createNotification은 클라이언트에서만 사용할 수 있습니다.')
  }
  
  validateCreateNotificationInput(input)

  const supabase = createBrowserClient()
  const insertData = {
    type: input.type,
    sender_type: input.senderType,
    sender_team_id: input.senderTeamId ?? null,
    sender_client_id: input.senderClientId ?? null,
    sender_profile_id: input.senderProfileId ?? null,
    target_team_id: input.targetTeamId ?? null,
    target_client_id: input.targetClientId ?? null,
    target_profile_id: input.targetProfileId ?? null,
    counsel_id: input.counselId ?? null,
    payload: input.payload ?? {},
    status: 'PENDING' as const,
    read_at: null,
    dedup_key: input.dedupKey ?? null,
  }

  const query = supabase
    .from('notifications' as any)
    .insert(insertData as any)
    .select()
    .single()

  const { data, error } = await query

  if (error) {
    if (input.dedupKey && (error as any).code === '23505') {
      const { data: existing, error: existingError } = await supabase
        .from('notifications' as any)
        .select('*')
        .eq('dedup_key', input.dedupKey)
        .limit(1)
        .maybeSingle()

      if (!existingError && existing) {
        return existing as unknown as Notification
      }
    }

    throw new Error(`알림 생성 실패: ${error.message}`)
  }

  return data as unknown as Notification
}

/**
 * 알림 상태 업데이트
 * 
 * @param notificationId - 알림 ID
 * @param status - 새로운 상태 (PENDING, ACCEPTED, DECLINED, CANCELED)
 * @param counselId - 프로젝트 ID (선택사항)
 * @returns 업데이트된 알림 객체
 */
export async function updateNotificationStatus(
  notificationId: number,
  status: NotificationStatus,
  counselId?: number
): Promise<Notification> {
  // 클라이언트에서만 실행
  if (typeof window === 'undefined') {
    throw new Error('updateNotificationStatus은 클라이언트에서만 사용할 수 있습니다.')
  }
  
  const supabase = createBrowserClient()
  const updateData: any = { 
    status,
    // PENDING이 아닌 상태로 변경 시 read_at도 업데이트 (읽음 처리)
    read_at: status !== 'PENDING' ? new Date().toISOString() : null,
    updated_at: new Date().toISOString(),
  }
  if (counselId) {
    updateData.counsel_id = counselId
  }

  const { data, error } = await supabase
    .from('notifications' as any)
    .update(updateData)
    .eq('id', notificationId)
    .select()
    .single()

  if (error) {
    throw new Error(`알림 상태 업데이트 실패: ${error.message}`)
  }

  return data as unknown as Notification
}

/**
 * 기업이 받은 알림 조회
 * 처리되지 않은 알림(PENDING)만 조회
 */
export async function getClientNotifications(
  clientId: string
): Promise<Notification[]> {
  // 클라이언트에서만 실행
  if (typeof window === 'undefined') {
    throw new Error('getClientNotifications은 클라이언트에서만 사용할 수 있습니다.')
  }
  
  const supabase = createBrowserClient()
  const { data, error } = await supabase
    .from('notifications' as any)
    .select('*')
    .eq('target_client_id', clientId)
    .eq('status', 'PENDING')
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`알림 조회 실패: ${error.message}`)
  }

  return (data || []).filter(
    (notif: any) => notif.status === 'PENDING'
  ) as unknown as Notification[]
}

/**
 * 팀이 받은 알림 조회
 * 처리되지 않은 알림(PENDING)만 조회
 */
export async function getTeamNotifications(
  teamId: number
): Promise<Notification[]> {
  // 클라이언트에서만 실행
  if (typeof window === 'undefined') {
    throw new Error('getTeamNotifications은 클라이언트에서만 사용할 수 있습니다.')
  }
  
  const supabase = createBrowserClient()
  const { data, error } = await supabase
    .from('notifications' as any)
    .select('*')
    .eq('target_team_id', teamId)
    .eq('status', 'PENDING')
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`알림 조회 실패: ${error.message}`)
  }

  return (data || []).filter(
    (notif: any) => notif.status === 'PENDING'
  ) as unknown as Notification[]
}

/**
 * 프리랜서 개인이 받은 알림 조회
 * 처리되지 않은 알림(PENDING)만 조회
 */
export async function getProfileNotifications(
  profileId: string
): Promise<Notification[]> {
  // 클라이언트에서만 실행
  if (typeof window === 'undefined') {
    throw new Error('getProfileNotifications은 클라이언트에서만 사용할 수 있습니다.')
  }
  
  const supabase = createBrowserClient()
  const { data, error } = await supabase
    .from('notifications' as any)
    .select('*')
    .eq('target_profile_id', profileId)
    .eq('status', 'PENDING')
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`알림 조회 실패: ${error.message}`)
  }

  return (data || []).filter(
    (notif: any) => notif.status === 'PENDING'
  ) as unknown as Notification[]
}
